export type RuleState = 'pending' | 'confirmed' | 'not_applicable';
export type Verdict = 'pass' | 'check' | 'fail';

export interface SubmissionRule {
  id: string;
  label: string;
  detail: string;
  state: RuleState;
}

export interface VideoSlot { id: string; label: string; repertoireIds: string[] }
export interface MediaProbe { container?: string; videoCodec?: string; audioCodec?: string; hasAudio?: boolean; frameRate?: number; bitrate?: number; durationSeconds?: number; width?: number; height?: number; error?: string }

export interface Program {
  slots?: VideoSlot[];
  presetId?: string;
  presetAppliedAt?: string;
  orientation?: 'landscape' | 'portrait' | null;
  requiredVideoCount?: number;
  allowedCodecs?: string[];
  fileNamePattern?: string;
  deadlineHistory?: { from: string; to: string; changedAt: string; reason: string }[];
  id: string;
  name: string;
  school: string;
  deadline: string;
  officialUrl: string;
  sourceRevision: string;
  sourceConfirmedAt?: string;
  mode?: 'quick' | 'application';
  /**
   * 요강 수치는 **사용자가 공식 요강에서 확인해 넣기 전까지 `null`** 이다.
   *
   * 기본값을 넣어 두면 제품이 확인하지 않은 것을 단정하게 된다. 실제로 그랬다 — 이 세 값은
   * `1GB / 15분 / video/mp4` 로 하드코딩돼 있었고 어느 화면에서도 고칠 수 없었는데,
   * SlideRoom 공식 한도는 250MB 라 **700MB 파일이 "파일 크기 통과"로 떴다**(4.3배). 반대로
   * 파일 선택기는 `.mov` 를 받으면서 판정은 fail 을 냈다. 불변조건은 "확인하지 못한 규칙과
   * 코덱은 확인 필요로 판정한다"인데 코어 판정이 그 조건을 정면으로 어기고 있었다.
   *
   * 같은 함수의 `rules` 는 처음부터 `state: 'pending'` 으로 시작한다. 규칙에는 "확인하세요"라고
   * 하면서 수치만 단정할 이유가 없다.
   */
  maxBytes: number | null;
  maxDurationSeconds: number | null;
  allowedContainers: string[] | null;
  maxWidth?: number | null;
  maxHeight?: number | null;
  rules: SubmissionRule[];
  createdAt: string;
}

export interface Take {
  probe?: MediaProbe;
  slotId?: string;
  replacesTakeId?: string;
  id: string;
  programId: string;
  title: string;
  fileName: string;
  mime: string;
  bytes: number;
  durationSeconds?: number;
  width?: number;
  height?: number;
  hash: string;
  createdAt: string;
  note: string;
}

export interface FinalLock {
  slotId?: string;
  lastMatchedAt?: string;
  programId: string;
  takeId: string;
  hash: string;
  lockedAt: string;
}

export interface ManualChecks {
  fullPlayback: boolean;
  audioSync: boolean;
  openingEnding: boolean;
  portalPreview: boolean;
}

export interface OfficialProof {
  programId: string;
  confirmationCode: string;
  submittedAt: string;
  note: string;
}

export interface WorkspaceState {
  schemaVersion?: 3;
  entitlement?: { kind: 'season_pass'; code: string; activatedAt: string; expiresAt: string };
  programs: Program[];
  takes: Take[];
  selectedProgramId?: string;
  selectedTakeId?: string;
  locks: FinalLock[];
  manualChecks: Record<string, ManualChecks>;
  proofs: OfficialProof[];
  preparations: Record<string, ProgramPreparation>;
}

export const emptyChecks = (): ManualChecks => ({
  fullPlayback: false,
  audioSync: false,
  openingEnding: false,
  portalPreview: false,
});

export const createProgram = (input: Pick<Program, 'name' | 'school' | 'deadline' | 'officialUrl'>): Program => ({
  ...input,
  id: crypto.randomUUID(),
  sourceRevision: new Date().toISOString().slice(0, 10),
  slots: [{ id: 'main', label: '보낼 영상', repertoireIds: [] }],
  maxBytes: null,
  maxDurationSeconds: null,
  allowedContainers: null,
  maxWidth: null,
  maxHeight: null,
  createdAt: new Date().toISOString(),
  rules: [
    { id: 'unedited', label: '영상 편집 허용 여부', detail: '자르기·이어 붙이기·음정 보정이 허용되는지 공식 요강에서 확인', state: 'pending' },
    { id: 'identity', label: '이름·수험번호 노출 규칙', detail: '영상 속 소개 또는 파일명 규칙 확인', state: 'pending' },
    { id: 'repertoire', label: '곡·악장 순서', detail: '지정곡과 제출 순서를 공식 요강에서 확인', state: 'pending' },
    { id: 'ai-disclosure', label: 'AI·후반작업 고지', detail: '기관별 생성형 AI 및 후반작업 고지 요구 확인', state: 'pending' },
  ],
});

export const sampleProgram = (): Program => ({
  ...createProgram({
    name: '가을 오디션 피아노 영상',
    school: '샘플 음악원',
    deadline: new Date(Date.now() + 12 * 86_400_000).toISOString().slice(0, 16),
    officialUrl: 'https://example.edu/audition',
  }),
  id: 'sample-program',
  sourceRevision: '샘플 — 실제 지원 시 공식 페이지로 교체',
});
import type { ProgramPreparation } from './audition-plan';
